import React from 'react';
import { Button, Icon } from '@repo/ui';
import { ConditionRuleBuilder } from './ConditionRuleBuilder';

interface ConditionRule {
  id: string;
  fieldId: number;
  fieldName: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'in' | 'not_in' | 'greater_than' | 'less_than';
  value: string | string[] | null;
}

interface ConditionGroup {
  id: string;
  conditions: ConditionRule[];
}

interface CustomFieldOption {
  id: number;
  name: string;
  field_type: string;
  parent_type: string;
  picklist_options?: Array<{ id: number; option: string }>;
}

interface ConditionGroupBuilderProps {
  group: ConditionGroup;
  groupIndex: number;
  customFields: CustomFieldOption[];
  onChange: (group: ConditionGroup) => void;
  onRemove: () => void;
  showOrConnector: boolean;
}

export function ConditionGroupBuilder({
  group,
  groupIndex,
  customFields,
  onChange,
  onRemove,
  showOrConnector,
}: ConditionGroupBuilderProps) {
  const addCondition = () => {
    const newCondition: ConditionRule = {
      id: `condition-${Date.now()}-${Math.random()}`,
      fieldId: 0,
      fieldName: '',
      operator: 'equals',
      value: '',
    };
    
    onChange({
      ...group,
      conditions: [...group.conditions, newCondition],
    });
  };

  const updateCondition = (conditionId: string, updates: Partial<ConditionRule>) => {
    onChange({
      ...group,
      conditions: group.conditions.map(c =>
        c.id === conditionId ? { ...c, ...updates } : c
      ),
    });
  };

  const removeCondition = (conditionId: string) => {
    onChange({
      ...group,
      conditions: group.conditions.filter(c => c.id !== conditionId),
    });
  };

  return (
    <div className="space-y-3">
      {/* OR Connector (only show between groups) */}
      {showOrConnector && (
        <div className="flex items-center justify-center py-2">
          <div className="flex items-center gap-3">
            <div className="h-px bg-xtnd-dark-200 dark:bg-xtnd-dark-500 w-16"></div>
            <span className="px-3 py-1 bg-xtnd-dark text-xtnd-white text-sm font-semibold rounded-md shadow-sm">
              OR
            </span>
            <div className="h-px bg-xtnd-dark-200 dark:bg-xtnd-dark-500 w-16"></div>
          </div>
        </div>
      )}

      {/* Group Container */}
      <div className="border-2 border-xtnd-blue/30 rounded-lg p-4 bg-white dark:bg-xtnd-dark-800">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-white">
              Group {groupIndex + 1}
            </span>
            <span className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
              (All conditions must match)
            </span>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
            title="Remove group"
          >
            <Icon name="HiTrash" className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          {group.conditions.map((condition, index) => (
            <div key={condition.id}>
              {/* AND Connector (only show between conditions within a group) */}
              {index > 0 && (
                <div className="flex items-center gap-2 py-2 pl-4">
                  <span className="px-2 py-0.5 bg-xtnd-blue text-white text-xs font-semibold rounded">
                    AND
                  </span>
                </div>
              )}
              
              <ConditionRuleBuilder
                condition={condition}
                customFields={customFields}
                onChange={updates => updateCondition(condition.id, updates)}
                onRemove={() => removeCondition(condition.id)}
              />
            </div>
          ))}

          {/* Add AND Condition Button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCondition}
            className="w-full border-xtnd-blue text-xtnd-blue hover:bg-xtnd-blue hover:text-white"
            leftIcon={<Icon name="HiPlus" />}
          >
            Add AND Condition
          </Button>
        </div>
      </div>
    </div>
  );
}

